import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './modal.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'
import { scrollLock, scrollUnlock } from 'elements/_shared/scroll-lock.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { animateTo, ElementAnimation } from 'elements/_shared/animations.js'


initializeIconsByRole({
  'cse-modal.error': { name: 'xmark', style: 'light' },
  'cse-modal.info': { name: 'info', style: 'light' },
  'cse-modal.success': { name: 'check', style: 'light' },
  'cse-modal.warning': { name: 'exclamation', style: 'light' },
})

export const modalSizes = ['default', 'large'] as const
export type modalSizesType = typeof modalSizes

export const modalVariants = ['default', 'error', 'info', 'success', 'warning'] as const
export type modalVariantTypes = typeof modalVariants

@customElement('cse-modal')
export class CSEModal extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style, fontStyles]

  @property({ type: String })
  variant: modalVariantTypes[number] = 'default'

  @property({ type: String })
  size: modalSizesType[number] = 'default'

  @property({ type: Boolean, attribute: 'no-backdrop' })
  noBackdrop = false

  @property({ type: Boolean, reflect: true, attribute: 'open' })
  isOpen = false

  @property({ type: Boolean, attribute: 'show-fullscreen' })
  showFullscreen = false

  @property({ type: Boolean, attribute: 'close-on-backdrop-click' })
  closeOnBackdropClick = false

  @query('dialog')
  dialog!: HTMLDialogElement

  private isAnimating = false

  private animations = {
    open: {
      keyframes: [
        { opacity: 0, scale: 0.8 },
        { opacity: 1, scale: 1 }
      ],
      options: { duration: 300, easing: 'ease-in-out' }
    },
    close: {
      keyframes: [
        { opacity: 1, scale: 1 },
        { opacity: 0, scale: 0.8 }
      ],
      options: { duration: 300, easing: 'ease-in-out' }
    }
  }

  override firstUpdated() {
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault()
    })
  }

  override async getUpdateComplete() {
    const result = await super.getUpdateComplete()
    await this.animationComplete
    return result
  }

  animationComplete: Promise<void> = Promise.resolve()

  open = async () => {
    if (this.isOpen || this.isAnimating) { return }

    let resolveAnimation!: () => void
    this.animationComplete = new Promise((resolve) => { resolveAnimation = resolve })

    this.dialog.showModal()
    this.isAnimating = true

    const animation: ElementAnimation = this.animations.open
    await animateTo(this.dialog, animation.keyframes, animation.options).then(() => {
      this.isAnimating = false
      this.isOpen = true
      resolveAnimation()
      scrollLock(this.dialog)
    })
  }

  close = async () => {
    if (!this.isOpen || this.isAnimating) { return }

    let resolveAnimation!: () => void
    this.animationComplete = new Promise((resolve) => { resolveAnimation = resolve })

    scrollUnlock(this.dialog)
    this.isAnimating = true

    const animation: ElementAnimation = this.animations.close
    await animateTo(this.dialog, animation.keyframes, animation.options).then(() => {
      this.isAnimating = false
      this.isOpen = false
      this.dialog.close()
      resolveAnimation()
    })
  }

  private handleDialogClick = (event: MouseEvent) => {
    if (!this.closeOnBackdropClick) { return }

    // clicks on the ::backdrop are dispatched on the dialog itself, so
    // detect them by checking the click position against the dialog bounds
    const { left, right, top, bottom } = this.dialog.getBoundingClientRect()
    const clickedOutside = event.clientX < left || event.clientX > right
      || event.clientY < top || event.clientY > bottom

    if (clickedOutside) { this.close() }
  }

  private renderHeader = () => html`
    <slot name="header" class="header font-heading-4"></slot>
  `

  private renderBody = () => html`
    <div class="body">
      ${this.variant !== 'default' ? html`
        <slot name="icon" class="icon">
          <cse-icon icon-role="${`cse-modal.${this.variant}`}" size="xl"></cse-icon>
        </slot>
      ` : nothing}
      <slot></slot>
    </div>
  `

  private renderFooter = () => html`
    <slot name="footer" class="footer">
      <cse-button
        @click="${this.close}"
        size="small"
        fit="fill"
      >
        Close
      </cse-button>
    </slot>
  `

  override render() {
    return html`
      <dialog
        @click="${this.handleDialogClick}"
        class="${classMap({
          'cse-modal': true,
          'no-backdrop': this.noBackdrop,
          [`size-${this.size}`]: true,
          [`variant-${this.variant}`]: true,
          'fullscreen': this.showFullscreen
        })}"
      >
        ${this.renderHeader()}
        ${this.renderBody()}
        ${this.renderFooter()}
      </dialog>
    `
  }
}


declare global {
  interface HTMLElementTagNameMap {
    'cse-modal': CSEModal;
  }
}
